import type SubtitleCue from './subtitleCue';
import type TranscriptWord from './transcriptWord';
import type TranscriptParametricRule from './transcriptParametricRule';
import type {
  ParametricRuleCheckCallback,
  ToStringCallback,
  TranscriptParametricSliderRule,
} from './transcriptParametricRule';
import type { RuleSettingOverride } from './ruleSettingOverride';
import {
  type TranscriptRuleSetting,
  isTranscriptSliderRuleSetting,
} from './transcriptRuleSetting';
import difference from 'lodash/difference';

export enum ParagraphRuleProperties {
  MAX_DURATION = 'maxDuration',
  MAX_CHARACTERS = 'maxCharacters',
  MAX_WORDS = 'maxWords',
  MAX_SILENCE = 'maxSilence',
  PUNCTUATION = 'punctuation',
  SPEAKER = 'speaker',
}

export enum SubtitleRuleProperties {
  MAX_DURATION = 'maxDuration',
  MAX_CHARACTERS = 'maxCharacters',
  MAX_WORDS = 'maxWords',
  MAX_SILENCE = 'maxSilence',
  LINE_BREAK_MAX_CHARACTERS = 'lineBreakMaxCharacters',
  PUNCTUATION = 'punctuation',
  SPEAKER = 'speaker',
}

export enum SpeakerRuleProperties {
  MAX_DURATION = 'maxDuration',
  MAX_CHARACTERS = 'maxCharacters',
  MAX_WORDS = 'maxWords',
  MAX_SILENCE = 'maxSilence',
  PUNCTUATION = 'punctuation',
  SPEAKER = 'speaker',
}

/**
 * Adding a property to the following 2 lists will automtically add a new settings in the interface
 * and save the property in tenant config
*/
export const paragraphRulesSettings: TranscriptRuleSetting[] = [
  {
    property: ParagraphRuleProperties.MAX_DURATION,
    label: 'TranscriptParametricRules.ParagraphMaxDuration',
    description: 'TranscriptParametricRules.ParagraphMaxDurationTooltip',
    min: 1,
    max: 60000,
    value: 60000,
    isActive: true,
  },
  {
    property: ParagraphRuleProperties.MAX_CHARACTERS,
    label: 'TranscriptParametricRules.ParagraphMaxCharacters',
    description: 'TranscriptParametricRules.ParagraphMaxCharactersTooltip',
    min: 1,
    max: 1000,
    value: 500,
    isActive: false,
  },
  {
    property: ParagraphRuleProperties.MAX_WORDS,
    label: 'TranscriptParametricRules.ParagraphMaxWords',
    description: 'TranscriptParametricRules.ParagraphMaxWordsTooltip',
    min: 1,
    max: 100,
    value: 50,
    isActive: false,
  },
  {
    property: ParagraphRuleProperties.MAX_SILENCE,
    label: 'TranscriptParametricRules.ParagraphMaxSilence',
    description: 'TranscriptParametricRules.ParagraphMaxSilenceTooltip',
    min: 1,
    max: 5000,
    value: 800,
    isActive: true,
  },
  {
    property: ParagraphRuleProperties.PUNCTUATION,
    label: 'TranscriptParametricRules.ParagraphPunctuation',
    description: 'TranscriptParametricRules.ParagraphPunctuationTooltip',
    isActive: false,
  },
  {
    property: ParagraphRuleProperties.SPEAKER,
    label: 'TranscriptParametricRules.ParagraphSpeaker',
    description: 'TranscriptParametricRules.ParagraphSpeakerTooltip',
    isActive: false,
  },
];

// Same shape as paragraph/subtitle, but only SPEAKER is active by default.
// Other rules are present so the tuning panel can layer them on top.
export const speakerRulesSettings: TranscriptRuleSetting[] = [
  {
    property: SpeakerRuleProperties.MAX_DURATION,
    label: 'TranscriptParametricRules.ParagraphMaxDuration',
    description: 'TranscriptParametricRules.ParagraphMaxDurationTooltip',
    min: 1,
    max: 60000,
    value: 60000,
    isActive: false,
  },
  {
    property: SpeakerRuleProperties.MAX_CHARACTERS,
    label: 'TranscriptParametricRules.ParagraphMaxCharacters',
    description: 'TranscriptParametricRules.ParagraphMaxCharactersTooltip',
    min: 1,
    max: 1000,
    value: 500,
    isActive: false,
  },
  {
    property: SpeakerRuleProperties.MAX_WORDS,
    label: 'TranscriptParametricRules.ParagraphMaxWords',
    description: 'TranscriptParametricRules.ParagraphMaxWordsTooltip',
    min: 1,
    max: 100,
    value: 50,
    isActive: false,
  },
  {
    property: SpeakerRuleProperties.MAX_SILENCE,
    label: 'TranscriptParametricRules.ParagraphMaxSilence',
    description: 'TranscriptParametricRules.ParagraphMaxSilenceTooltip',
    min: 1,
    max: 5000,
    value: 800,
    isActive: false,
  },
  {
    property: SpeakerRuleProperties.PUNCTUATION,
    label: 'TranscriptParametricRules.ParagraphPunctuation',
    description: 'TranscriptParametricRules.ParagraphPunctuationTooltip',
    isActive: false,
  },
  {
    property: SpeakerRuleProperties.SPEAKER,
    label: 'TranscriptParametricRules.ParagraphSpeaker',
    description: 'TranscriptParametricRules.ParagraphSpeakerTooltip',
    isActive: true,
  },
];

export const subtitleRulesSettings: TranscriptRuleSetting[] = [
  {
    property: SubtitleRuleProperties.MAX_DURATION,
    label: 'TranscriptParametricRules.SubtitleMaxDuration',
    description: 'TranscriptParametricRules.SubtitleMaxDurationTooltip',
    min: 1,
    max: 30000,
    value: 3000,
    isActive: false,
  },
  {
    property: SubtitleRuleProperties.MAX_CHARACTERS,
    label: 'TranscriptParametricRules.SubtitleMaxCharacters',
    description: 'TranscriptParametricRules.SubtitleMaxCharactersTooltip',
    min: 1,
    max: 200,
    value: 100,
    isActive: false,
  },
  {
    property: SubtitleRuleProperties.MAX_WORDS,
    label: 'TranscriptParametricRules.SubtitleMaxWords',
    description: 'TranscriptParametricRules.SubtitleMaxWordsTooltip',
    min: 1,
    max: 20,
    value: 8,
    isActive: true,
  },
  {
    property: SubtitleRuleProperties.MAX_SILENCE,
    label: 'TranscriptParametricRules.SubtitleMaxSilence',
    description: 'TranscriptParametricRules.SubtitleMaxSilenceTooltip',
    min: 1,
    max: 5000,
    value: 800,
    isActive: true,
  },
  {
    property: SubtitleRuleProperties.LINE_BREAK_MAX_CHARACTERS,
    label: 'TranscriptParametricRules.SubtitleLineBreak',
    description: 'TranscriptParametricRules.SubtitleLineBreakTooltip',
    min: 1,
    max: 200,
    value: 50,
    isActive: false,
  },
  {
    property: SubtitleRuleProperties.PUNCTUATION,
    label: 'TranscriptParametricRules.SubtitlePunctuation',
    description: 'TranscriptParametricRules.SubtitlePunctuationTooltip',
    isActive: true,
  },
  {
    property: SubtitleRuleProperties.SPEAKER,
    label: 'TranscriptParametricRules.SubtitleSpeaker',
    description: 'TranscriptParametricRules.SubtitleSpeakerTooltip',
    isActive: false,
  },
];

/*
  A slider rule's predicate compares against its numeric threshold. These
  dictionary entries are templates that take the threshold as a parameter;
  toParametricRule binds the resolved value into the rule instance's checkRule so
  the instance is self-contained (a plain ParametricRuleCheckCallback).
*/
type SliderRuleCheckTemplate = (
  currentSubtitleCue: SubtitleCue,
  prev: TranscriptWord,
  cur: TranscriptWord,
  value: number,
) => boolean;

interface SliderCallbacks {
  checkRule: SliderRuleCheckTemplate;
  formattedValue: ToStringCallback;
}

interface BooleanCallbacks {
  checkRule: ParametricRuleCheckCallback;
}

interface MergedRuleSettingsResult {
  hasUpdated: boolean;
  mergedSettings: TranscriptRuleSetting[];
}

const maxCharacterCheckRule = (
    currentSubtitleCue: SubtitleCue,
    prev: TranscriptWord,
    cur: TranscriptWord,
    value: number,
) => {
  const length =
      currentSubtitleCue.getCharacterLength() + cur.content.length;
  return length >= value;
};

const sliderCheckCallbacks = {
  maxDuration: {
    checkRule: (
      currentSubtitleCue: SubtitleCue,
      prev: TranscriptWord,
      cur: TranscriptWord,
      value: number,
    ) => {
      const [firstWord] = currentSubtitleCue.words;
      if (firstWord === undefined) {
        return false;
      }
      const duration = cur.endTime - firstWord.startTime;
      return duration >= value;
    },
    formattedValue: (value: number) => `${(value / 1000).toFixed(2)} s`,
  },
  maxWords: {
    checkRule: (
      currentSubtitleCue: SubtitleCue,
      prev: TranscriptWord,
      cur: TranscriptWord,
      value: number,
    ) => {
      return currentSubtitleCue.words.length >= value;
    },
    formattedValue: (value: number) => `${value}`,
  },
  maxCharacters: {
    checkRule: maxCharacterCheckRule,
    formattedValue: (value: number) => `${value}`,
  },
  maxSilence: {
    checkRule: (
      currentSubtitleCue: SubtitleCue,
      prev: TranscriptWord,
      cur: TranscriptWord,
      value: number,
    ) => {
      return cur.startTime - prev.endTime >= value;
    },
    formattedValue: (value: number) => `${value / 1000} s`,
  },
  lineBreakMaxCharacters: {
    checkRule: maxCharacterCheckRule,
    formattedValue: (value: number) => `${value}`,
  },
} satisfies Readonly<Record<string, SliderCallbacks>>;

const booleanCheckCallbacks = {
  punctuation: {
    checkRule: (
      currentSubtitleCue: SubtitleCue,
      prev: TranscriptWord,
      cur: TranscriptWord,
    ) => {
      return /[.?!]$/.test(prev.content);
    },
  },
  speaker: {
    checkRule: (
      currentSubtitleCue: SubtitleCue,
      prev: TranscriptWord,
      cur: TranscriptWord,
    ) => {
      /*
        Normalise so cosmetic variants don't trigger phantom boundaries; words
        with no speaker continue the current run.
      */
      const p = normaliseSpeaker(prev.speaker);
      const c = normaliseSpeaker(cur.speaker);
      if (p === undefined || c === undefined) {
        return false;
      }
      return p !== c;
    },
  },
} satisfies Readonly<Record<string, BooleanCallbacks>>;

function normaliseSpeaker(raw: string | undefined): string | undefined {
  if (typeof raw !== 'string') {
    return undefined;
  }
  const trimmed = raw.trim().toLowerCase();
  return trimmed.length === 0 ? undefined : trimmed;
}

type SliderRuleProperty = keyof typeof sliderCheckCallbacks;
type BooleanRuleProperty = keyof typeof booleanCheckCallbacks;

/*
  `Object.prototype.hasOwnProperty.call(...)` is an own-key check (excludes
  inherited members like `toString`/`constructor`) that also narrows the type.
  Prefer `Object.hasOwn(...)` once the CEP runtime floor (Chromium 88) is no
  longer a concern — it needs Chromium 93+.
*/
const isSliderRuleProperty = (property: string): property is SliderRuleProperty => {
  return Object.prototype.hasOwnProperty.call(sliderCheckCallbacks, property);
};

const isBooleanRuleProperty = (property: string): property is BooleanRuleProperty => {
  return Object.prototype.hasOwnProperty.call(booleanCheckCallbacks, property);
};

function toParametricRule(setting: TranscriptRuleSetting, override: RuleSettingOverride | undefined): TranscriptParametricRule {
  const isActive: boolean = override === undefined ? setting.isActive : override.isActive;
  if (isTranscriptSliderRuleSetting(setting)) {
    if (!isSliderRuleProperty(setting.property)) {
      throw new Error(`Missing slider callbacks for transcript rule property "${setting.property}".`);
    }

    const callbacks = sliderCheckCallbacks[setting.property];
    /*
      `checkRule` reads `rule.value` at invocation rather than capturing the
      initial value, so live in-place mutation of the threshold (e.g. the editor
      slider's v-model) takes effect on the next re-segmentation. The arrow only
      dereferences `rule` when called, by which point the object is assigned.
    */
    const rule: TranscriptParametricSliderRule = {
      property: setting.property,
      isActive,
      label: setting.label,
      ...(setting.description !== undefined && { description: setting.description }),
      checkRule: (currentSubtitleCue, prev, cur) => callbacks.checkRule(currentSubtitleCue, prev, cur, rule.value),
      value: override?.value ?? setting.value,
      min: setting.min,
      max: setting.max,
      formattedValue: callbacks.formattedValue,
    };
    return rule;
  }

  if (!isBooleanRuleProperty(setting.property)) {
    throw new Error(`Missing boolean callbacks for transcript rule property "${setting.property}".`);
  }

  const callbacks = booleanCheckCallbacks[setting.property];
  return {
    property: setting.property,
    isActive,
    label: setting.label,
    ...(setting.description !== undefined && { description: setting.description }),
    checkRule: callbacks.checkRule,
  };
}

export const getParametricRules = (
  ruleSettings: TranscriptRuleSetting[],
  overrides: Record<string, RuleSettingOverride> | undefined,
): TranscriptParametricRule[] => {
  const rules: TranscriptParametricRule[] = [];
  for (const setting of ruleSettings) {
    rules.push(toParametricRule(setting, overrides?.[setting.property]));
  }
  return rules;
};

export const getMergedRuleSettings = (
  tenantConfigSettings: TranscriptRuleSetting[] | undefined,
  defaultSettings: TranscriptRuleSetting[],
  propertyList: string[],
): MergedRuleSettingsResult => {
  if (!tenantConfigSettings) {
    return {
      hasUpdated: true,
      mergedSettings: [...defaultSettings],
    };
  }

  const knownProperties = new Set(propertyList);
  const supportedTenantSettings = tenantConfigSettings.filter((tenantSetting) => {
    return knownProperties.has(tenantSetting.property);
  });

  // Merge descriptions from default settings into tenant settings
  const settingsWithDescriptions = supportedTenantSettings.map(tenantSetting => {
    const defaultSetting = defaultSettings.find(ds => ds.property === tenantSetting.property);
    if (defaultSetting?.description && !tenantSetting.description) {
      return { ...tenantSetting, description: defaultSetting.description };
    }
    return tenantSetting;
  });

  const modifiedSettings = addMissingProperties(settingsWithDescriptions, defaultSettings, propertyList);
  if (modifiedSettings === null) {
    return {
      hasUpdated: supportedTenantSettings.length !== tenantConfigSettings.length,
      mergedSettings: settingsWithDescriptions,
    };
  }

  return {
    hasUpdated: true,
    mergedSettings: modifiedSettings,
  };
};

const addMissingProperties = (
  settingsObject: TranscriptRuleSetting[],
  objectsToChooseFrom: TranscriptRuleSetting[],
  propertyList: string[],
): TranscriptRuleSetting[] | null => {
  const presentProperties = settingsObject.map((currentObject) => currentObject.property);
  const missingProperties = difference(propertyList, presentProperties);

  if (missingProperties.length < 1) {
    return null;
  }

  const modifiedSettingsObject = [...settingsObject];
  objectsToChooseFrom.forEach((currentObject) => {
    if (missingProperties.includes(currentObject.property)) {
      modifiedSettingsObject.push(currentObject);
    }
  });

  return modifiedSettingsObject;
};
